// @flow weak

import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { useQuery, useMutation } from '@apollo/react-hooks';

import { RouterSelectors } from '@zapier/toolbox-redux-framework';
import { isSubUrlOf } from '@zapier/url-utils';
import { UniversalNav, useUniversalNavContext } from '@zapier/universal-nav';
import { AlertBanner } from '@zapier/universal-layout';
import { LEGACY_BUILDER_BASE_URL } from '@zapier/product-dev-platform';

import { useTrackingContext } from '@zapier/toolbox-tracking';
import { CurrentAccountIdProvider } from '@zapier/common-hooks';
import { Account, Profile } from '@zapier/shared-entities';
import { HeldRunsBannerWrapper } from '@zapier/held-runs-embed';
import maybeRender from 'app/common/components/maybeRender';
import { isProductionEnv } from 'app/common/CommonUtils';

import PlatformHeaderContainer from './PlatformHeaderContainer';

let hasWarnedAboutHeader = false;

const bannerlessRoutes = [
  '/engine/auth',
  '/sign-up',
  '/app/login',
  '/app/editor',
  '/app/onboarding',
  '/app/instant',
  '/app/settings',
];
const shouldRenderAlertBanner = ({ currentUrl = '' } = {}) => {
  return bannerlessRoutes.every(bannerlessRoute => {
    return !currentUrl.includes(bannerlessRoute);
  });
};

function shouldHeldRunsBannerBeVisible(currentPathname: string) {
  const allowedRoutes = [
    /app\/connections/,
    /app\/dashboard/,
    /app\/history/,
    /app\/zaps/,
  ];

  return allowedRoutes.some(regex => regex.test(currentPathname));
}

// Wrapper around `UniversalNav` to prevent unnecessary props
// from being passed to it, which can be confusing. Defined
// here so it is only defined once and will not incessantly
// remount when the header renrenders.
const UniversalNavHeader = props => {
  const { trackEvent } = useTrackingContext();
  const { sidebarWidth } = useUniversalNavContext();

  return (
    <CurrentAccountIdProvider currentAccountId={props.accountId}>
      {shouldRenderAlertBanner(props) && (
        <AlertBanner
          trackEvent={trackEvent}
          useMutation={useMutation}
          useQuery={useQuery}
        />
      )}
      <UniversalNav
        // Use `key` to force remount after logged in state changes.
        // This helps to ensure that the logged in/out version of
        // the navigation renders correctly.
        key={props.isLoggedIn ? 'logged-in' : 'logged-out'}
      />
      {props.isHeldRunsBannerVisible && (
        <div style={{ paddingLeft: sidebarWidth }}>
          <HeldRunsBannerWrapper />
        </div>
      )}
    </CurrentAccountIdProvider>
  );
};

const inferHeaderConfig = ({ currentUrl }) => {
  // Since currentUrl is set asyncronously and certain pages use route fetch,
  // it can take awhile to resolve causing a flicker in the Header. Therefore,
  // we'll just default to whatever the current location before it resolves.
  // This will become unnecessary when SSR takes over.
  if (!currentUrl) currentUrl = window.location.pathname;

  // This is a totally different header from the rest of the app,
  // so handle this case first.
  if (
    isSubUrlOf(currentUrl, '/developer') ||
    isSubUrlOf(currentUrl, '/zap-templates')
  ) {
    return {
      CustomComponent: PlatformHeaderContainer,
    };
  }

  return {
    CustomComponent: UniversalNavHeader,
  };
};

// Some urls should hide the header because it is a waste of resources if we
// attempt to render it (because it will make async requests). Previously we
// managed this by deciding to render the header on a Django template. When
// the client then moved to a page that did not contain a header (because we
// need an injection point), `refreshChrome` would create a `#header` element
// and inject the header there. We've moved this up to `Root.js` using a portal
// combined with `<EnsureElement>`.
// The problem now is that the Header will always render. So this logic below
// replicates what we previously did by discluding the header in a Django
// template.
export const shouldHeaderBeVisible = currentUrl => {
  const predicates = [
    url => !isSubUrlOf(url, '/app/editor'),
    url => !isSubUrlOf(url, '/shared'),
    url => !isSubUrlOf(url, '/engine/auth/start'),
    url =>
      !(
        isSubUrlOf(url, `${LEGACY_BUILDER_BASE_URL}/app`) &&
        _.endsWith(url, 'json-editor')
      ),
    url =>
      !(
        isSubUrlOf(url, `${LEGACY_BUILDER_BASE_URL}/app`) &&
        _.endsWith(url, 'scripting')
      ),
    url => !isSubUrlOf(url, '/app/settings'),
  ];

  return predicates.every(predicate => predicate(currentUrl));
};

const HeaderWrapper = props => {
  const { CustomComponent } = props;

  return <CustomComponent {...props} />;
};

const mapStateToProps = state => {
  const currentUrl = RouterSelectors.currentUrl(state) || '';
  const isLoggedIn = Profile.selectors.all.isLoggedIn(state);
  const headerConfig = inferHeaderConfig({
    currentUrl,
  });

  const { CustomComponent } = headerConfig;

  return {
    accountId: Account.selectors.all.currentAccountId(state),
    currentUrl,
    CustomComponent,
    isHeaderVisible: shouldHeaderBeVisible(currentUrl),
    isHeldRunsBannerVisible: shouldHeldRunsBannerBeVisible(currentUrl),
    isLoggedIn,
    numberOfApps: _.get(window, ['BASE_CONFIG', 'numberOfApps'], 0),
  };
};

const ReduxContainer = _.flow(
  maybeRender(({ isHeaderVisible, hasHeaderFromServer, currentUrl }) => {
    // This condition covers a situation where we don't want to render the
    // header, but we also cannot currently check if the URL is in our
    // whitelist (see `shouldHeaderBeVisible`) because we don't have the current
    // url information yet. This happens because `currentUrl` is asynchronous
    // so on startup it does not have a value.
    // A possible fix for this hack would be to have the server send us the
    // current location of the client, this would be closer to what real SSR
    // would do.
    // This fix prevents the header from rendering and then disappearing on
    // our whitelisted pages.
    if (_.isEmpty(currentUrl) && hasHeaderFromServer === false) {
      if (!isProductionEnv() && hasWarnedAboutHeader === false) {
        hasWarnedAboutHeader = true;

        console.warn(
          'Heads up! The header was not rendered on this page when the app started up because it did not meet the criteria to do so. If you believe this is incorrect, you may need to include the header in the Django template for this page. See `app/viewport/components/HeaderContainer.js` for more details.'
        );
      }

      return false;
    }

    return isHeaderVisible === true;
  }),
  connect(mapStateToProps, null)
)(HeaderWrapper);

const HeaderContainer = props => {
  const {
    isSidebarControlledByTopbar,
    shouldRenderUniversalNav,
  } = useUniversalNavContext();
  const hasUniversalNavFromServer = props.serverHeaderType === 'universal-nav';
  return (
    <ReduxContainer
      isSidebarControlledByTopbar={isSidebarControlledByTopbar}
      shouldRenderUniversalNav={
        shouldRenderUniversalNav || hasUniversalNavFromServer
      }
    />
  );
};

export default HeaderContainer;
